import React, { useState } from 'react';
import TwoLinesWithPercentTable from './twoLineChartWithTable';
import { Tooltip } from 'flowbite-react';
import SpinnerCustom from 'components/spinnerCustom';
import { getItemShrinkageByCategory, getItemShrinkageByLocation } from 'api/dashboardApi';
import { useSelector } from 'react-redux';
import { orgSelector } from 'redux/selectors';

type reportTableProps = {
  title?: string;
  data?: any;
  color?: string;
  linearFrom?: string;
  linearTo?: string;
  loading?: boolean;
};

/* ----------------- HELPERS ----------------- */
const Caret: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    className={`h-4 w-4 text-slate-500 transition-transform ${open ? 'rotate-90' : ''}`}
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707A1 1 0 018.707 5.293l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

/* ----------------- TABLE COMPONENT ----------------- */
export default function ReportTable(props: reportTableProps) {
  const { title, data, loading } = props;
  const { organizationId } = useSelector(orgSelector);

  const [openLoc, setOpenLoc] = React.useState<Set<string>>(new Set());
  const [openCat, setOpenCat] = React.useState<Set<string>>(new Set());

  const [selectedItem, setSelectedItem] = React.useState<{ path: string; item: any } | null>(null);

  const [categoriesByLoc, setCategoriesByLoc] = useState<Record<string, any[]>>({});
  const [itemsByCat, setItemsByCat] = useState<Record<string, any[]>>({});

  const [loadingLocCat, setLoadingLocCat] = useState<Record<string, boolean>>({});
  const [loadingCatItem, setLoadingCatItem] = useState<Record<string, boolean>>({});

  const setLocCatLoading = (locId: string, value: boolean) => {
    setLoadingLocCat(prev => ({ ...prev, [locId]: value }));
  };

  const setCatItemLoading = (catKey: string, value: boolean) => {
    setLoadingCatItem(prev => ({ ...prev, [catKey]: value }));
  };

  const toggleLoc = async (loc: any) => {
    const locId = loc.id as string;

    const isOpen = openLoc.has(locId);
    if (isOpen) {
      setOpenLoc(prev => {
        const n = new Set(prev);
        n.delete(locId);
        return n;
      });
      return;
    }

    setOpenLoc(prev => {
      const n = new Set(prev);
      n.add(locId);
      return n;
    });

    try {
      setLocCatLoading(locId, true);
      const res = await getItemShrinkageByLocation(organizationId, locId);
      const categories = res?.data ?? [];
      setCategoriesByLoc(prev => ({
        ...prev,
        [locId]: categories,
      }));
    } catch (err) {
      console.error('Error load categories by location', err);
    } finally {
      setLocCatLoading(locId, false);
    }
  };

  const toggleCat = async (locId: string, cat: any) => {
    const catId = cat.id as string;
    const catKey = `${locId}/${catId}`;

    const isOpen = openCat.has(catKey);
    if (isOpen) {
      setOpenCat(prev => {
        const n = new Set(prev);
        n.delete(catKey);
        return n;
      });
      return;
    }

    setOpenCat(prev => {
      const n = new Set(prev);
      n.add(catKey);
      return n;
    });

    try {
      setCatItemLoading(catKey, true);
      const res = await getItemShrinkageByCategory(locId, catId);
      const items = res?.data ?? [];
      setItemsByCat(prev => ({
        ...prev,
        [catKey]: items,
      }));
    } catch (err) {
      console.error('Error load items by category', err);
    } finally {
      setCatItemLoading(catKey, false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 mt-4 mr-4">
      {/* HEADER */}
      <div className="px-4 py-3 w-full flex items-center">
        <div className="flex-1">
          <h3 className="text-xl font-semibold text-slate-800">{title}</h3>
        </div>
        <div className="w-24 text-right font-medium text-slate-600">Shrinkage %</div>
        <div className="w-24 text-right font-medium text-slate-600">Sales %</div>
      </div>

      {loading ? (
        <SpinnerCustom className="h-[220px]" />
      ) : (
        <>
          {/* BODY: scrollable */}
          <div className="max-h-[600px] overflow-y-auto" style={{ willChange: 'scroll-position', transform: 'translateZ(0)' }}>
            {data?.map((loc: any) => {
              const isOpenLoc = openLoc.has(loc.id);
              const categories = categoriesByLoc[loc.id] || [];
              const isLocLoading = loadingLocCat[loc.id];

              return (
                <div key={loc.id} className="border-t border-slate-100">
                  {/* --- LOCATION ROW --- */}
                  <button
                    className="w-full flex items-center px-4 py-3 hover:bg-slate-50"
                    onClick={() => toggleLoc(loc)}
                  >
                    <div className="flex-1 flex items-center gap-2 text-left">
                      <Caret open={isOpenLoc} />
                      <span className="font-medium">{loc.name}</span>
                    </div>
                    <div className="w-24 text-left justify-items-end">
                      <Tooltip
                        placement="top-start"
                        trigger="hover"
                        content={
                          <div className="max-w-[360px] text-white text-xs">
                            <div className="mt-0.5">
                              <span className="font-semibold">Shrinkage Percentage:</span>
                              &nbsp;% of stock that was lost due to shrinkage at this location over the last 4
                              weeks.
                            </div>
                            <div className="mt-0.5">
                              <span className="font-semibold">Formula: </span>
                              Sum of all <b>Shrinkage</b> / <b>Receipts</b> over the 4 weeks
                            </div>
                          </div>
                        }
                        className="[&>div]:bg-slate-900 [&>div]:rounded-md [&>div]:px-3 [&>div]:py-2"
                      >
                        <span className="font-medium">{loc.valueShrinkage} %</span>
                      </Tooltip>
                    </div>
                    <div className="w-24 text-left justify-items-end">
                      <Tooltip
                        placement="top-start"
                        trigger="hover"
                        content={
                          <div className="max-w-[360px] text-white text-xs">
                            <div className="mt-0.5">
                              <span className="font-semibold">Sales Percentage:</span>
                              &nbsp;% of stock that was sold at this location over the last 4 weeks.
                            </div>
                            <div>
                              <span className="font-semibold">Formula: </span>
                              Sum of all <b>Sales</b> in SL / Sum of all <b>Positive</b> entries in SL over the 4
                              weeks
                            </div>
                          </div>
                        }
                        className="[&>div]:bg-slate-900 [&>div]:rounded-md [&>div]:px-3 [&>div]:py-2"
                      >
                        <span className="font-medium">{loc.valueSale} %</span>
                      </Tooltip>
                    </div>
                  </button>

                  {/* --- CATEGORY ROWS --- */}
                  {isOpenLoc && (
                    <>
                      {isLocLoading && (
                        <div className="pl-8 pr-4 py-3">
                          <SpinnerCustom />
                        </div>
                      )}

                      {!isLocLoading &&
                        categories?.map((cat: any) => {
                          const catKey = `${loc.id}/${cat.id}`;
                          const isOpenCat = openCat.has(catKey);
                          const items = itemsByCat[catKey] || [];
                          const isCatLoading = loadingCatItem[catKey];

                          return (
                            <div key={catKey} className="border-t border-slate-100">
                              <button
                                className="w-full pl-8 pr-4 py-3 flex items-center hover:bg-slate-50"
                                onClick={() => toggleCat(loc.id, cat)}
                              >
                                <div className="flex-1 flex items-center gap-2">
                                  <Caret open={isOpenCat} />
                                  <span>{cat.name}</span>
                                </div>
                                <div className="w-24 text-left justify-items-end">
                                  <Tooltip
                                    placement="top-start"
                                    trigger="hover"
                                    content={
                                      <div className="max-w-[360px] text-white text-xs">
                                        <div className="mt-0.5">
                                          <span className="font-semibold">Shrinkage Percentage:</span>
                                          &nbsp;% of stock that was lost due to shrinkage at this category over the
                                          last 4 weeks.
                                        </div>
                                        <div className="mt-0.5">
                                          <span className="font-semibold">Formula: </span>
                                          Sum of all <b>Shrinkage</b> / <b>Receipts</b> over the 4 weeks
                                        </div>
                                      </div>
                                    }
                                    className="[&>div]:bg-slate-900 [&>div]:rounded-md [&>div]:px-3 [&>div]:py-2"
                                  >
                                    <span className="font-medium">{cat.valueShrinkage} %</span>
                                  </Tooltip>
                                </div>
                                <div className="w-24 text-left justify-items-end">
                                  <Tooltip
                                    placement="top-start"
                                    trigger="hover"
                                    content={
                                      <div className="max-w-[360px] text-white text-xs">
                                        <div className="mt-0.5">
                                          <span className="font-semibold">Sales Percentage:</span>
                                          &nbsp;% of stock that was sold at this category over the last 4 weeks.
                                        </div>
                                        <div>
                                          <span className="font-semibold">Formula: </span>
                                          Sum of all <b>Sales</b> in SL / Sum of all <b>Positive</b> entries in SL
                                          over the 4 weeks
                                        </div>
                                      </div>
                                    }
                                    className="[&>div]:bg-slate-900 [&>div]:rounded-md [&>div]:px-3 [&>div]:py-2"
                                  >
                                    <span className="font-medium">{cat.valueSale} %</span>
                                  </Tooltip>
                                </div>
                              </button>

                              {/* --- ITEM ROWS --- */}
                              {isOpenCat && (
                                <>
                                  {isCatLoading && (
                                    <div className="pl-12 pr-4 py-3">
                                      <SpinnerCustom />
                                    </div>
                                  )}

                                  {!isCatLoading &&
                                    items?.map((item: any) => {
                                      const itemPath = `${loc.id}/${cat.id}/${item.id}`;
                                      const opened = selectedItem?.path === itemPath;

                                      return (
                                        <div key={item.id} className="border-t border-slate-100">
                                          <button
                                            className={`w-full pl-12 pr-4 py-3 flex items-center hover:bg-slate-50 ${
                                              opened ? 'bg-slate-50' : ''
                                            }`}
                                            onClick={() =>
                                              setSelectedItem(
                                                opened ? null : { path: itemPath, item }
                                              )
                                            }
                                          >
                                            <div className="flex-1 flex items-center gap-2">
                                              <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                                              <span>{item.name}</span>
                                            </div>
                                            <div className="w-24 text-left justify-items-end">
                                              <Tooltip
                                                placement="top-start"
                                                trigger="hover"
                                                content={
                                                  <div className="max-w-[360px] text-white text-xs">
                                                    <div className="mt-0.5">
                                                      <span className="font-semibold">
                                                        Shrinkage Percentage:
                                                      </span>
                                                      &nbsp;% of stock that was lost due to shrinkage at this item
                                                      over the last 4 weeks.
                                                    </div>
                                                    <div className="mt-0.5">
                                                      <span className="font-semibold">Formula: </span>
                                                      Sum of all <b>Shrinkage</b> / <b>Receipts</b> over the 4 weeks
                                                    </div>
                                                  </div>
                                                }
                                                className="[&>div]:bg-slate-900 [&>div]:rounded-md [&>div]:px-3 [&>div]:py-2"
                                              >
                                                <span className="font-medium">{item.valueShrinkage} %</span>
                                              </Tooltip>
                                            </div>
                                            <div className="w-24 text-left justify-items-end">
                                              <Tooltip
                                                placement="top-start"
                                                trigger="hover"
                                                content={
                                                  <div className="max-w-[360px] text-white text-xs">
                                                    <div className="mt-0.5">
                                                      <span className="font-semibold">
                                                        Sales Percentage:
                                                      </span>
                                                      &nbsp;% of stock that was sold at this item over the last 4
                                                      weeks.
                                                    </div>
                                                    <div>
                                                      <span className="font-semibold">Formula: </span>
                                                      Sum of all <b>Sales</b> in SL / Sum of all{' '}
                                                      <b>Positive</b> entries in SL over the 4 weeks
                                                    </div>
                                                  </div>
                                                }
                                                className="[&>div]:bg-slate-900 [&>div]:rounded-md [&>div]:px-3 [&>div]:py-2"
                                              >
                                                <span className="font-medium">{item.valueSale} %</span>
                                              </Tooltip>
                                            </div>
                                          </button>

                                          {opened && (
                                            <div className="px-14 pb-4">
                                              <TwoLinesWithPercentTable
                                                labels={item.weeks}
                                                sales={item.sales}
                                                shrink={item.shrink}
                                                positiveTotal={item.positiveTotal}
                                              />
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                </>
                              )}
                            </div>
                          );
                        })}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
